import { useState, useEffect, useMemo } from 'react';

export const useSearchableMakes = () => {
  const [allMakes, setAllMakes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // Lista de fallback con marcas populares en USA
  const fallbackMakes = [
    // Big 3 estadounidenses + Tesla
    { Make_ID: 1, Make_Name: 'FORD' },
    { Make_ID: 2, Make_Name: 'CHEVROLET' },
    { Make_ID: 3, Make_Name: 'GMC' },
    { Make_ID: 4, Make_Name: 'DODGE' },
    { Make_ID: 5, Make_Name: 'JEEP' },
    { Make_ID: 6, Make_Name: 'RAM' },
    { Make_ID: 7, Make_Name: 'TESLA' },
    
    // Marcas japonesas más populares en USA
    { Make_ID: 8, Make_Name: 'TOYOTA' },
    { Make_ID: 9, Make_Name: 'HONDA' },
    { Make_ID: 10, Make_Name: 'NISSAN' },
    { Make_ID: 11, Make_Name: 'MAZDA' },
    { Make_ID: 12, Make_Name: 'SUBARU' },
    
    // Marcas coreanas populares en USA
    { Make_ID: 13, Make_Name: 'HYUNDAI' },
    { Make_ID: 14, Make_Name: 'KIA' },
    
    // Marcas alemanas de lujo populares en USA
    { Make_ID: 15, Make_Name: 'BMW' },
    { Make_ID: 16, Make_Name: 'MERCEDES-BENZ' },
    { Make_ID: 17, Make_Name: 'AUDI' },
    { Make_ID: 18, Make_Name: 'VOLKSWAGEN' }
  ];

  const fetchMakes = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔄 Fetching vehicle makes from NHTSA API...');
      
      const response = await fetch('https://vpic.nhtsa.dot.gov/api/vehicles/getallmakes?format=json', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Error fetching makes: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.Results && Array.isArray(data.Results)) {
        console.log('✅ Found', data.Results.length, 'makes from NHTSA API');
        
        // Filtrar y ordenar las marcas más populares en USA
        const usPopularMakes = [
          // Marcas estadounidenses (Big 3 + Tesla)
          'FORD', 'CHEVROLET', 'GMC', 'CADILLAC', 'BUICK', 'CHRYSLER', 'DODGE', 'JEEP', 'RAM', 'TESLA',
          
          // Marcas japonesas más populares en USA
          'TOYOTA', 'HONDA', 'NISSAN', 'LEXUS', 'ACURA', 'INFINITI', 'MAZDA', 'SUBARU', 'MITSUBISHI',
          
          // Marcas coreanas populares en USA
          'HYUNDAI', 'KIA', 'GENESIS',
          
          // Marcas alemanas de lujo populares en USA
          'BMW', 'MERCEDES-BENZ', 'AUDI', 'VOLKSWAGEN', 'PORSCHE',
          
          // Marcas europeas de lujo
          'VOLVO', 'JAGUAR', 'LAND ROVER', 'MINI',
          
          // Marcas de superdeportivos y lujo
          'FERRARI', 'LAMBORGHINI', 'MASERATI', 'BENTLEY', 'ROLLS-ROYCE', 'ASTON MARTIN',
          
          // Marcas eléctricas emergentes
          'RIVIAN', 'LUCID', 'POLESTAR',
          
          // Marcas comerciales y de trabajo
          'FREIGHTLINER', 'INTERNATIONAL', 'PETERBILT', 'KENWORTH', 'MACK',
          
          // Marcas de motocicletas populares en USA
          'HARLEY-DAVIDSON', 'INDIAN', 'YAMAHA', 'KAWASAKI', 'SUZUKI', 'HONDA',
          
          // Marcas de vehículos recreativos
          'WINNEBAGO', 'FLEETWOOD', 'COACHMEN', 'JAYCO', 'FOREST RIVER'
        ];
        
        // Obtener las marcas populares en USA primero
        const usPopularMakesData = usPopularMakes.map(makeName => 
          data.Results.find(make => make.Make_Name === makeName)
        ).filter(Boolean);
        
        // Obtener el resto de marcas, excluyendo las populares en USA
        const otherMakes = data.Results
          .filter(make => !usPopularMakes.includes(make.Make_Name))
          .sort((a, b) => a.Make_Name.localeCompare(b.Make_Name));
        
        // Combinar: marcas populares en USA primero, luego el resto
        const allMakes = [...usPopularMakesData, ...otherMakes];
        
        console.log('🎯 Processed makes for USA market:', allMakes.length, 'total (', usPopularMakesData.length, 'US popular +', otherMakes.length, 'others)');
        setAllMakes(allMakes);
        
      } else {
        throw new Error('Invalid data format received from NHTSA API');
      }
      
    } catch (err) {
      console.error('❌ Error fetching vehicle makes from API:', err);
      setError(err.message);
      setAllMakes(fallbackMakes);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar marcas basado en el término de búsqueda
  const filteredMakes = useMemo(() => {
    if (!searchTerm.trim()) {
      // Si no hay búsqueda, mostrar solo las marcas populares (primeras 50)
      return allMakes.slice(0, 50);
    }
    
    const searchLower = searchTerm.toLowerCase();
    return allMakes.filter(make => 
      make.Make_Name.toLowerCase().includes(searchLower)
    ).slice(0, 100); // Limitar a 100 resultados para mejor rendimiento
  }, [allMakes, searchTerm]);

  // Obtener marcas populares para mostrar inicialmente
  const popularMakes = useMemo(() => {
    return allMakes.slice(0, 20); // Solo las primeras 20 marcas populares
  }, [allMakes]);

  useEffect(() => {
    fetchMakes();
  }, []);

  // Función para obtener el Make_ID de una marca por nombre
  const getMakeIdByName = (makeName) => {
    const make = allMakes.find(m => m.Make_Name === makeName);
    return make ? make.Make_ID : null;
  };

  return {
    allMakes,
    filteredMakes,
    popularMakes,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    showDropdown,
    setShowDropdown,
    getMakeIdByName,
    refetch: fetchMakes
  };
};
